import { useState, useEffect } from "react";
import Notification from '../../components/Notification';
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ApiHelper } from "../../utils/ApiHelper";
import { COMPANY_DETAIL_OF_USER, UPDATE_COMPANY_DETAIL, UPLOAD_COMPANY_LOGO } from "../../lib/API_KEYS";

export default function UpdateAdditionalInfo() {
  const [formData, setFormData] = useState({
    website: "",
    linkedIn: "",
    fax: "",
    email: "",
    countryCode: "",
    phone: "",
    address: "",
    name: "",
    aboutUs: "",
    industry: [],
    otherIndustry: "",
    contactName: "",
    designation: "",
    contactEmail: "",
    contactCountryCode: "",
    contactPhone: "",
    businessType: "",
    regNumber: "",
    taxId: "",
    turnover: "",
    bankName: "",
    accountNumber: "",
    ifsc: "",
    product: "",
    orderVolume: "",
    frequency: "",
    quality: "",
    paymentTerms: "",
    shipping: "",
    specialReq: "",
    referral: "",
    objective: "",
    preferences: "",
    status: "pending",
    confirmCheck: false
  });
  const [isChanged, setIsChanged] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const navigate = useNavigate();

  const INDUSTRIES = [
    "Automotive",
    "Aerospace",
    "Consumer Goods",
    "Electronics & Communications",
    "Medical Equipment",
    "Industrial Machinery",
    "Others"
  ];

  const isEqual = (obj1, obj2) => {
    return JSON.stringify(obj1, Object.keys(obj1).sort()) === JSON.stringify(obj2, Object.keys(obj2).sort());
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await new ApiHelper().FetchFromServer(
          ...COMPANY_DETAIL_OF_USER
        );
        console.log("Fetch response:", res.data);
        if (res.data) {
          setFormData(res.data);
          setInitialData(res.data);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        toast.error("Failed to fetch form data");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (initialData) {
      setIsChanged(!isEqual(formData, initialData));
    }
  }, [formData, initialData]);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value
    }));
  };

  const handleSelect = (e) => {
    const value = e.target.value;

    if (!value) return;

    // If selecting Others → only add "Others"
    if (value === "Others") {
      setFormData(prev => ({
        ...prev,
        industry: [...prev.industry, value]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        industry: [...prev.industry.filter(i => i !== "Others"), value],
        otherIndustry: ""
      }));
    }
  };

  const removeChip = (item) => {
    setFormData(prev => {
      const updated = prev.industry.filter(i => i !== item);

      return {
        ...prev,
        industry: updated,
        otherIndustry: item === "Others" ? "" : prev.otherIndustry
      };
    });
  };




  const onSubmit = async (e) => {
    const { confirmCheck, website, logo, linkedIn, email, aboutUs, address, name, industry, contactName, designation, contactEmail, phone, contactPhone, countryCode, contactCountryCode, ...rest } = formData;
    e.preventDefault();

    if (!confirmCheck) {
      toast.error('Please confirm before submitting.');
      return;
    }


    // Check industry array is not empty
    if (!industry || industry.length === 0) {
      toast.error('Please select at least one industry.');
      return;
    }

    // Check all other required fields
    if (!website || !linkedIn || !email || !aboutUs || !address || !name || !contactName || !designation || !contactEmail || !contactPhone || !countryCode || !contactCountryCode || !phone) {
      toast.error('Please fill all the required fields.');
      return;
    }
    try {
      let logo = formData.logo || '';
      if (uploadedLogo) {
        const uploadRes = await new ApiHelper().FetchFromServer(
          ...UPLOAD_COMPANY_LOGO,
          undefined,
          { fileName: uploadedLogo.name, fileType: uploadedLogo.type, fileSize: uploadedLogo.size },
        );

        if (uploadRes.data?.isError || !uploadRes.data?.signedRequest) {
          console.error("Failed to get upload URL");
          return;
        }

        // Upload to S3
        await fetch(uploadRes.data.signedRequest, {
          method: "PUT", body: uploadedLogo, headers: {
            "Content-Type": uploadedLogo.type,
          },
        });
        logo = uploadRes.data.fileUrl;
      }

      // Check for uploaded logo (required for onboarding)
      if (!logo) {
        toast.error('Please upload a company logo.');
        return;
      }

      // If resubmitting after Query Raised, change status back to In Review
      const finalData = {
        ...formData,
        logo,
        status: 'In Review',
        type: 'Buyer'
      };

      await new ApiHelper().FetchFromServer(
        ...UPDATE_COMPANY_DETAIL,
        undefined,
        finalData
      );
      toast.success('Submitted successfully!');
      setTimeout(() => navigate("/buyer-dashboard"), 2000);
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Submission Failed");
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File must be <= 2MB.');
      return;
    }
    setUploadedLogo(file);
  };

  const onBack = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="container py-4">
        <h4 className="mb-4">Update Additional info</h4>

        {/* Show admin concern if status is Query Raised */}
        {formData.status === "Query Raised" && formData.concern && (
          <div className="alert alert-warning mb-4" role="alert">
            <h6 className="alert-heading">
              <strong>⚠️ Admin Feedback:</strong>
            </h6>
            <p className="mb-0">{formData.concern}</p>
            <hr />
            <small className="text-muted">
              Please address the concerns above and resubmit your application.
            </small>
          </div>
        )}

        <hr />
        <h6 className='mb-4'>Company information</h6>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label htmlFor="website" className="form-label">Website <span style={{ color: "red" }}>*</span></label>
            <input id="website" value={formData.website} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your Website" />
          </div>
          <div className="col-md-6">
            <label htmlFor="linkedIn" className="form-label">LinkedIn <span style={{ color: "red" }}>*</span></label>
            <input id="linkedIn" value={formData.linkedIn} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your LinkedIn profile URL" />
          </div>
          <div className="col-md-6">
            <label htmlFor="fax" className="form-label">FAX Number</label>
            <input id="fax" value={formData.fax} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your FAX number" />
          </div>
          <div className="col-md-6">
            <label htmlFor="email" className="form-label">Email Address <span style={{ color: "red" }}>*</span></label>
            <input id="email" value={formData.email} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your Email address" />
          </div>
          <div className="col-md-6">
            <label htmlFor="countryCode" className="form-label">Country Code <span style={{ color: "red" }}>*</span></label>
            <input id="countryCode" value={formData.countryCode} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your country code" />
          </div>
          <div className="col-md-6">
            <label htmlFor="phone" className="form-label">Phone Number <span style={{ color: "red" }}>*</span></label>
            <input id="phone" value={formData.phone} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your phone number" />
          </div>
          <div className="col-md-6">
            <label htmlFor="address" className="form-label">Company Address <span style={{ color: "red" }}>*</span></label>
            <input id="address" value={formData.address} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your company address" />
          </div>
          <div className="col-md-6">
            <label htmlFor="name" className="form-label">Company Name <span style={{ color: "red" }}>*</span></label>
            <input id="name" value={formData.name} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your company name" />
          </div>
          <div className="col-md-6">
            <label className="form-label">Company Logo <span style={{ color: "red" }}>*</span></label>
            {formData.logo && !uploadedLogo && (
              <div className="mb-2">
                <img src={formData.logo} alt="Company Logo" style={{ maxHeight: 80 }} />
              </div>
            )}
            <input type="file" accept="image/*" className="form-control" onChange={handleLogoChange} />
          </div>
          <div className="col-md-6">
            <label htmlFor="aboutUs" className="form-label">About Us <span style={{ color: "red" }}>*</span></label>
            <textarea id="aboutUs" name="aboutus" rows={3} value={formData.aboutUs} onChange={handleChange} className="form-control custom-focus" placeholder="Enter About your company" />
          </div>
        </div>
        <hr />
        <h6 className='my-4'>Industry <span style={{ color: "red" }}>*</span></h6>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="d-flex flex-wrap mb-1 gap-2">
              {formData?.industry?.map(item => (
                <span className="chip" key={item}>
                  {item}
                  <span className="chip-close" onClick={() => removeChip(item)}>✕</span>
                </span>
              ))}
            </div>
            <select className="form-select" onChange={handleSelect}>
              <option value="">Select Industry</option>
              {INDUSTRIES.filter(item => !formData.industry.includes(item))
                .map(item => (
                  <option key={item} value={item}>{item}</option>
                ))}
            </select>
          </div>

          {formData.industry.includes("Others") && (
            <div className="col-md-6">
              <label htmlFor="otherIndustry" className="form-label">Specify Industry</label>
              <input
                className="form-control mt-2"
                placeholder="Specify industry"
                value={formData.otherIndustry}
                onChange={(e) => setFormData(prev => ({ ...prev, otherIndustry: e.target.value }))}
              />
            </div>
          )}

        </div>


        <hr />
        <h6 className='my-4'>Contact Details </h6>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label htmlFor="contactName" className="form-label">Contact Name <span style={{ color: "red" }}>*</span></label>
            <input id="contactName" value={formData.contactName} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Contact Name" />
          </div>
          <div className="col-md-6">
            <label htmlFor="designation" className="form-label">Title/Designation <span style={{ color: "red" }}>*</span></label>
            <input id="designation" value={formData.designation} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Contact Title/Designation" />
          </div>
          <div className="col-md-6">
            <label htmlFor="contactEmail" className="form-label">Contact Email<span style={{ color: "red" }}>*</span></label>
            <input id="contactEmail" value={formData.contactEmail} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Contact Email" />
          </div>
          <div className="col-md-6">
            <label htmlFor="contactCountryCode" className="form-label">Country Code<span style={{ color: "red" }}>*</span></label>
            <input id="contactCountryCode" value={formData.contactCountryCode} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Country Code" />
          </div>
          <div className="col-md-6">
            <label htmlFor="contactPhone" className="form-label">Contact Phone <span style={{ color: "red" }}>*</span></label>
            <input id="contactPhone" value={formData.contactPhone} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Contact Phone" />
          </div>
        </div>
        <hr />
        <h6 className='my-4'>Type of Business Entity</h6>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label htmlFor="businessType" className="form-label">Business Type</label>
            <select id="businessType" value={formData.businessType} onChange={handleChange} className="form-select custom-focus">
              <option value="">Select</option>
              <option value="Sole Proprietorship">Sole Proprietorship</option>
              <option value="Partnership">Partnership</option>
              <option value="Private Limited">Private Limited</option>
              <option value="Public Limited">Public Limited</option>
              <option value="LLC">LLC</option>
              <option value="Corporation">Corporation</option>
            </select>
          </div>
        </div>
        <hr />
        <h6 className='my-4'>Registration</h6>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label htmlFor="regNumber" className="form-label">Business Registration Number</label>
            <input id="regNumber" value={formData.regNumber} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Business Registration Number" />
          </div>
          <div className="col-md-6">
            <label htmlFor="taxId" className="form-label">Tax Identification Number (TIN)</label>
            <input id="taxId" value={formData.taxId} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Tax Identification Number" />
          </div>
        </div>

        {/* <Notification heading={'The company is already exists at our platform'} subHeading={'Please contact your company owner or contact our tech support if you think this is a mistake'} text={'Contact Support'} variant={'empty'} link={'/'} /> */}
        <hr />
        <h6 className='my-4'>Financial Information</h6>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label htmlFor="turnover" className="form-label">Annual Turnover</label>
            <input id="turnover" value={formData.turnover} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Annual Turnover/Revenue" />
          </div>
          <div className="col-md-6">
            <label htmlFor="bankName" className="form-label">Bank Name</label>
            <input id="bankName" value={formData.bankName} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Bank Name" />
          </div>
          <div className="col-md-6">
            <label htmlFor="accountNumber" className="form-label">Account Number</label>
            <input id="accountNumber" value={formData.accountNumber} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Account Number" />
          </div>
          <div className="col-md-6">
            <label htmlFor="ifsc" className="form-label">IFSC/Routing Number</label>
            <input id="ifsc" value={formData.ifsc} onChange={handleChange} className="form-control custom-focus" placeholder="Enter IFSC Code or Routing Number" />
          </div>
        </div>

        <hr />
        <h6 className='my-4'>Buyer Requirements</h6>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label htmlFor="product" className="form-label">Type of Products/Service Needed</label>
            <select
              id="product"
              value={formData.product}
              onChange={handleChange}
              className="form-select custom-focus"
            >
              <option value="">Select product or service</option>
              <option value="Raw Materials">Raw Materials</option>
              <option value="Manufacturing Services">Manufacturing Services</option>
              <option value="IT Services">IT Services</option>
              <option value="Logistics">Logistics</option>
              <option value="Consulting">Consulting</option>
            </select>
          </div>

          <div className="col-md-6">
            <label htmlFor="orderVolume" className="form-label">Expected Order Volume</label>
            <input id="orderVolume" value={formData.orderVolume} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Expected Order Volume" />
          </div>
          <div className="col-md-6">
            <label htmlFor="frequency" className="form-label">Frequency of Orders</label>
            <input id="frequency" value={formData.frequency} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Frequency of Orders" />
          </div>
          <div className="col-md-6">
            <label htmlFor="quality" className="form-label">Quality Standards/Specifications</label>
            <input id="quality" value={formData.quality} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Quality Standards/Specifications" />
          </div>
          <div className="col-md-6">
            <label htmlFor="paymentTerms" className="form-label">Payment Terms</label>
            <input id="paymentTerms" value={formData.paymentTerms} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Payment Terms" />
          </div>
          <div className="col-md-6">
            <label htmlFor="shipping" className="form-label">Shipping Preferences</label>
            <input id="shipping" value={formData.shipping} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Shipping Preferences" />
          </div>
          <div className="col-12">
            <label htmlFor="specialReq" className="form-label">Any Special Requirements or Instructions</label>
            <textarea id="specialReq" value={formData.specialReq} onChange={handleChange} className="form-control custom-focus" rows="2" placeholder="Enter Any Special Requirements or Instructions" />
          </div>
        </div>

        <hr />
        <h6 className='my-4'>Additional Information</h6>
        <div className="row g-3 mb-4">
          <div className="col-md-12">
            <label htmlFor="referral" className="form-label">How did you hear about us?</label>
            <input id="referral" value={formData.referral} onChange={handleChange} className="form-control custom-focus" placeholder="How did you hear about us?" />
          </div>
          <div className="col-md-12">
            <label htmlFor="objective" className="form-label">Your Objective</label>
            <input id="objective" value={formData.objective} onChange={handleChange} className="form-control custom-focus" placeholder="e.g., Sourcing, Supply Chain Optimization, Cost Reduction, etc." />
          </div>
          <div className="col-12">
            <label htmlFor="preferences" className="form-label">Specific Requirements</label>
            <textarea id="preferences" value={formData.preferences} onChange={handleChange} className="form-control custom-focus" rows="4" placeholder="Enter Any Specific Requirements or Preferences" />
          </div>
        </div>

        <div className="form-check mb-4">
          <input type="checkbox" className="form-check-input" id="confirmCheck" checked={formData.confirmCheck} onChange={handleChange} />
          <label className="form-check-label" htmlFor="confirmCheck">
            By submitting this form, I confirm that the information provided is true and accurate to the best of my knowledge.
          </label>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-outline-dark" onClick={onBack}>Cancel</button>
          <button
            onClick={onSubmit}
            disabled={!isChanged}
            className={`btn 
    ${isChanged ? "btn-dark" : "btn-secondary disabled"}`}
          >
            Send
          </button>

        </div>

        <style>{`
        .custom-focus:focus {
          border-color: #000000;
          box-shadow: 0 0 0 0.25rem rgba(0, 0, 0, 0.25);
          outline: none;
          transition: border-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
        }
        .chip {
          padding: 6px 12px;
          background: #f3f3f3;
          border-radius: 20px;
        }
        .chip-close {
          margin-left: 8px;
          cursor: pointer;
        }
      `}</style>
      </div >
      <ToastContainer />
    </>
  );
}
